import { useCallback, useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import dayjs from "dayjs";
import {
  AlertTriangle, CheckCircle2, ChevronDown, FileWarning, Loader2, MapPin,
  ShieldCheck, Undo2, UploadCloud, X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { TierBadge } from "@/components/oem/TierBadge";
import { KpiSectionTable } from "@/components/oem/OemWidgets";

const monthInputValue = (m) => m || "";

/* ------------------------------ preview card ------------------------------ */
const UnparsedRowsEditor = ({ rows, manual, setManual, fileKey }) => (
  <div className="mt-3 border border-[hsl(var(--status-amber)/0.4)] rounded-lg p-3 bg-[hsl(var(--status-amber)/0.05)]">
    <p className="text-xs font-medium flex items-center gap-1.5 mb-2">
      <FileWarning size={13} className="text-[hsl(var(--status-amber))]" />
      {rows.length} KPI row{rows.length !== 1 ? "s" : ""} could not be parsed — enter the scores manually (optional)
    </p>
    <div className="space-y-2">
      {rows.map((r, i) => {
        const key = `${r.section}|${r.no}`;
        const m = manual[key] || {};
        const set = (field, value) => setManual((prev) => ({
          ...prev, [key]: { ...prev[key], section: r.section, no: r.no, [field]: value },
        }));
        return (
          <div key={key} className="grid grid-cols-2 md:grid-cols-6 gap-2 items-center text-xs">
            <span className="md:col-span-2 truncate" title={r.name}>{r.no} {r.name}</span>
            <Input className="h-7 text-xs" placeholder="Max" value={m.max ?? ""}
              onChange={(e) => set("max", e.target.value)}
              data-testid={`oem-manual-max-${fileKey}-${i}`} />
            <Input className="h-7 text-xs" placeholder="Target" value={m.target ?? ""}
              onChange={(e) => set("target", e.target.value)} />
            <Input className="h-7 text-xs" placeholder="Achieved" value={m.achievement ?? ""}
              onChange={(e) => set("achievement", e.target.value)} />
            <Input className="h-7 text-xs" placeholder="Score" value={m.score ?? ""}
              onChange={(e) => set("score", e.target.value)}
              data-testid={`oem-manual-score-${fileKey}-${i}`} />
          </div>
        );
      })}
    </div>
  </div>
);

const PreviewFileCard = ({ file, decision, setDecision }) => {
  const [manual, setManual] = useState({});
  const [showKpis, setShowKpis] = useState(false);
  const month = decision.month || file.eval_month || "";
  const skip = decision.action === "skip";

  useEffect(() => {
    const rows = Object.values(manual).filter((r) => r.score !== undefined && r.score !== "");
    setDecision((d) => ({
      ...d,
      manual_rows: rows.map((r) => ({
        section: r.section, no: r.no,
        max: r.max ? Number(r.max) : undefined,
        target: r.target, achievement: r.achievement,
        score: r.score !== undefined && r.score !== "" ? Number(r.score) : undefined,
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manual]);

  if (file.error) {
    return (
      <Card className="border-[hsl(var(--cx-crimson)/0.4)]" data-testid={`oem-preview-error-${file.filename}`}>
        <CardContent className="py-4 flex items-start gap-2">
          <X size={15} className="text-[hsl(var(--cx-crimson))] mt-0.5" />
          <div>
            <p className="text-sm font-medium">{file.filename}</p>
            <p className="text-xs text-[hsl(var(--cx-crimson))]">{file.error}</p>
            <p className="text-[0.65rem] text-muted-foreground mt-1">This file will be skipped — the rest of the batch is unaffected.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const summary = file.summary || {};
  const ma = (summary.monthly_achievement || {})[file.eval_month];
  const unmatched = !file.matched_outlet;

  return (
    <Card className={cn(skip && "opacity-60", unmatched && "border-[hsl(var(--cx-crimson)/0.4)]")}
      data-testid={`oem-preview-card-${file.dealer_code || file.filename}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <CardTitle className="text-sm font-medium">{file.filename}</CardTitle>
          <Badge variant="outline" className="tabular text-[0.65rem]">{file.dealer_code || "no code"}</Badge>
          {file.matched_outlet ? (
            <span className="flex items-center gap-1 text-xs text-[hsl(var(--status-green))]" data-testid={`oem-matched-${file.dealer_code}`}>
              <MapPin size={12} /> {file.matched_outlet.name}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-[hsl(var(--cx-crimson))] font-medium" data-testid="oem-unrecognized-code">
              <AlertTriangle size={12} /> Unrecognized dealer code
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <label className="text-[0.65rem] text-muted-foreground">Scorecard month</label>
            <Input type="month" min="2026-01" className="h-7 w-[140px] text-xs tabular"
              value={monthInputValue(month)}
              onChange={(e) => setDecision((d) => ({ ...d, month: e.target.value }))}
              data-testid={`oem-month-input-${file.dealer_code || "x"}`} />
            <Button size="sm" variant={skip ? "default" : "outline"} className="h-7 text-xs"
              onClick={() => setDecision((d) => ({ ...d, action: skip ? "commit" : "skip" }))}
              data-testid={`oem-skip-toggle-${file.dealer_code || "x"}`}>
              {skip ? "Skipped — include" : "Skip file"}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        <div className="flex items-center gap-4 flex-wrap text-xs">
          <span>Monthly Achievement: <b className="tabular text-base">{ma ?? "—"}</b></span>
          <TierBadge tier={(summary.tiers || {})[file.eval_month]} />
          <span className="text-muted-foreground tabular">National #{summary.national_rank ?? "—"} · Regional #{summary.regional_rank ?? "—"}</span>
          <span className="text-muted-foreground tabular">
            Sales {(file.section_totals?.Sales || {}).score ?? "—"}/{(file.section_totals?.Sales || {}).max ?? "—"} ·
            Service {(file.section_totals?.Service || {}).score ?? "—"}/{(file.section_totals?.Service || {}).max ?? "—"}
          </span>
          <span className="text-muted-foreground">{file.kpi_count} KPIs parsed</span>
        </div>

        {file.already_exists && !skip && (
          <Alert className="py-2 border-[hsl(var(--status-amber)/0.5)] bg-[hsl(var(--status-amber)/0.06)]" data-testid="oem-exists-warning">
            <AlertTitle className="text-xs flex items-center gap-1.5">
              <AlertTriangle size={13} className="text-[hsl(var(--status-amber))]" />
              A scorecard for this location and month already exists
            </AlertTitle>
            <AlertDescription className="text-[0.68rem]">
              Committing will replace it (the old version is kept and restored on undo). Use "Skip file" to cancel just this one.
            </AlertDescription>
          </Alert>
        )}

        {(file.warnings || []).map((w, i) => (
          <p key={i} className="text-[0.68rem] text-[hsl(var(--status-amber))] flex items-start gap-1.5" data-testid={`oem-warning-${i}`}>
            <AlertTriangle size={11} className="mt-0.5 shrink-0" /> {w}
          </p>
        ))}

        {(file.notes || []).length > 0 && (
          <p className="text-[0.68rem] text-muted-foreground italic">Note: {file.notes.join(" · ")}</p>
        )}

        {(file.unparsed_rows || []).length > 0 && !skip && (
          <UnparsedRowsEditor rows={file.unparsed_rows} manual={manual} setManual={setManual} fileKey={file.key} />
        )}

        <Collapsible open={showKpis} onOpenChange={setShowKpis}>
          <CollapsibleTrigger asChild>
            <button className="flex items-center gap-1.5 text-xs font-medium text-[hsl(var(--cx-navy))] hover:opacity-80"
              data-testid={`oem-preview-kpis-toggle-${file.dealer_code || "x"}`}>
              Full extracted KPI preview
              <ChevronDown size={13} className={cn("transition-transform duration-200", showKpis && "rotate-180")} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2 mt-2">
            {Object.entries(file.sections || {}).map(([sec, pillars]) => (
              <KpiSectionTable key={sec} section={sec} pillars={pillars}
                month={file.eval_month} testBase={`oem-preview-${file.dealer_code || "x"}`} />
            ))}
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};

/* ------------------------------ batch history ----------------------------- */
const BatchHistory = ({ history, onRollback, loading }) => (
  <Card data-testid="oem-batch-history">
    <CardHeader className="pb-2">
      <CardTitle className="font-serif text-base font-normal">Scorecard Upload History</CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      {loading ? <Skeleton className="h-16" /> : history.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">No scorecard batches yet.</p>
      ) : (
        <Table className="cx-table">
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">When</TableHead>
              <TableHead>By</TableHead>
              <TableHead>Scorecards</TableHead>
              <TableHead>Months</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right pr-4" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.map((b) => (
              <TableRow key={b.id} data-testid={`oem-batch-row-${b.id.slice(0, 8)}`}>
                <TableCell className="pl-4 text-xs tabular">{dayjs(b.at).format("DD MMM YYYY, HH:mm")}</TableCell>
                <TableCell className="text-xs">{b.by}</TableCell>
                <TableCell className="text-xs">
                  {b.files.length} file{b.files.length !== 1 ? "s" : ""}
                  <span className="text-muted-foreground"> · {b.files.slice(0, 3).map((f) => f.dealer_code).join(", ")}{b.files.length > 3 ? "…" : ""}</span>
                </TableCell>
                <TableCell className="text-xs tabular">{[...new Set(b.files.map((f) => f.month))].join(", ")}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={cn("text-[0.62rem] uppercase",
                    b.status === "committed"
                      ? "text-[hsl(var(--status-green))] border-[hsl(var(--status-green)/0.35)]"
                      : "text-muted-foreground")}>
                    {b.status === "rolled_back" ? "Rolled back" : "Committed"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right pr-4">
                  {b.status === "committed" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => onRollback(b)} data-testid={`oem-rollback-btn-${b.id.slice(0, 8)}`}>
                      <Undo2 size={12} className="mr-1" /> Undo
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);

/* --------------------------------- main tab -------------------------------- */
export const OemUploadTab = () => {
  const inputRef = useRef();
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null); // {stage_id, files}
  const [decisions, setDecisions] = useState({}); // key -> {action, month, manual_rows}
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get("/oem/batches");
      setHistory(r.data.batches || []);
    } catch { /* non-blocking */ }
    setHistoryLoading(false);
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setParsing(true);
    setResult(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await api.post("/oem/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      const d = {};
      r.data.files.forEach((f) => {
        d[f.key] = { action: f.error || !f.matched_outlet ? "skip" : "commit", month: f.eval_month || "" };
      });
      setDecisions(d);
    } catch (e) {
      toast.error("Parse failed", { description: errMsg(e) });
    }
    setParsing(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const body = {
        stage_id: preview.stage_id,
        decisions: preview.files.filter((f) => !f.error).map((f) => ({
          key: f.key,
          action: decisions[f.key]?.action || "skip",
          month: decisions[f.key]?.month || f.eval_month,
          manual_rows: decisions[f.key]?.manual_rows || [],
        })),
      };
      const r = await api.post("/oem/commit", body);
      setResult(r.data);
      setPreview(null);
      setDecisions({});
      loadHistory();
      toast.success(`${r.data.files.length} scorecard${r.data.files.length !== 1 ? "s" : ""} committed`, {
        description: "One-click undo is available in the history below.",
      });
    } catch (e) {
      toast.error("Commit failed", { description: errMsg(e) });
    }
    setCommitting(false);
  };

  const doRollback = async () => {
    setRollingBack(true);
    try {
      await api.post(`/oem/batches/${rollbackTarget.id}/rollback`);
      toast.success("Batch rolled back", { description: "Replaced scorecards were restored." });
      setRollbackTarget(null);
      setResult(null);
      loadHistory();
    } catch (e) {
      toast.error("Rollback failed", { description: errMsg(e) });
    }
    setRollingBack(false);
  };

  const commitCount = preview
    ? preview.files.filter((f) => !f.error && decisions[f.key]?.action === "commit").length : 0;

  return (
    <div className="space-y-4" data-testid="oem-upload-tab">
      {!preview && (
        <Card>
          <CardContent className="py-8">
            <button
              className="w-full border-2 border-dashed rounded-xl py-10 flex flex-col items-center gap-2 hover:border-[hsl(var(--cx-navy))] hover:bg-[hsl(var(--accent))] transition-colors"
              onClick={() => inputRef.current?.click()}
              disabled={parsing}
              data-testid="oem-upload-dropzone">
              {parsing ? <Loader2 size={22} className="animate-spin text-[hsl(var(--cx-navy))]" />
                : <UploadCloud size={22} className="text-[hsl(var(--cx-navy))]" />}
              <span className="text-sm font-medium">{parsing ? "Parsing scorecards…" : "Select Kia CX scorecard files"}</span>
              <span className="text-xs text-muted-foreground">
                PDF or Excel · one file per dealer code · several files at once is fine · nothing commits until you confirm
              </span>
            </button>
            <input ref={inputRef} type="file" multiple accept=".pdf,.xlsx,.xlsm,.xls" className="hidden"
              onChange={(e) => onFiles(e.target.files)} data-testid="oem-file-input" />
            <p className="text-[0.65rem] text-muted-foreground mt-3">
              The dealer code in each file's header is matched to Settings → Locations. Historical backfill from
              January 2026 is supported in any order — trends and comparisons recompute automatically.
            </p>
          </CardContent>
        </Card>
      )}

      {preview && (
        <>
          <Alert className="py-2.5" data-testid="oem-preview-banner">
            <AlertTitle className="text-sm flex items-center gap-1.5">
              <ShieldCheck size={15} className="text-[hsl(var(--cx-navy))]" />
              Preview — verify every value before committing
            </AlertTitle>
            <AlertDescription className="text-xs">
              {preview.files.length} file{preview.files.length !== 1 ? "s" : ""} parsed. The month field is editable per file.
              Committing replaces any existing scorecard for the same location + month (undo restores it).
            </AlertDescription>
          </Alert>
          {preview.files.map((f) => (
            <PreviewFileCard key={f.key} file={f}
              decision={decisions[f.key] || {}}
              setDecision={(updater) => setDecisions((prev) => ({
                ...prev, [f.key]: typeof updater === "function" ? updater(prev[f.key] || {}) : updater,
              }))} />
          ))}
          <div className="flex items-center gap-2">
            <Button onClick={commit} disabled={committing || commitCount === 0} data-testid="oem-commit-btn">
              {committing ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}
              Commit {commitCount} scorecard{commitCount !== 1 ? "s" : ""}
            </Button>
            <Button variant="outline" onClick={() => { setPreview(null); setDecisions({}); }} data-testid="oem-cancel-btn">
              Cancel
            </Button>
          </div>
        </>
      )}

      {result && (
        <Alert className="border-[hsl(var(--status-green)/0.4)] bg-[hsl(var(--status-green)/0.05)]" data-testid="oem-commit-result">
          <AlertTitle className="text-sm flex items-center gap-1.5">
            <CheckCircle2 size={15} className="text-[hsl(var(--status-green))]" />
            {result.files.length} scorecard{result.files.length !== 1 ? "s" : ""} committed
          </AlertTitle>
          <AlertDescription className="text-xs">
            {result.files.map((f) => `${f.outlet_name} ${f.month} — ${Math.round(f.monthly_achievement ?? 0)} (${f.tier || "—"})`).join(" · ")}
            {result.errors?.length > 0 && (
              <span className="block text-[hsl(var(--cx-crimson))] mt-1">
                {result.errors.map((e) => `${e.filename || e.key}: ${e.error}`).join(" · ")}
              </span>
            )}
            <Button size="sm" variant="outline" className="h-7 text-xs mt-2 block"
              onClick={() => setRollbackTarget({ id: result.id })} data-testid="oem-undo-last-btn">
              <Undo2 size={12} className="mr-1 inline" /> Undo this batch
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <BatchHistory history={history} loading={historyLoading} onRollback={setRollbackTarget} />

      <Dialog open={!!rollbackTarget} onOpenChange={(o) => !o && setRollbackTarget(null)}>
        <DialogContent data-testid="oem-rollback-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg">Undo this scorecard batch?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Scorecards from this batch are removed and any versions they replaced are restored.
            Trends, leaderboards and reports recompute automatically.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRollbackTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doRollback} disabled={rollingBack} data-testid="oem-rollback-confirm-btn">
              {rollingBack ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Undo2 size={14} className="mr-1.5" />}
              Undo batch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
